/* Main split-view: resume form on left, sticky result on right.
 *
 * All form-control choices come from the catalogs prop (loaded once at boot
 * from /catalogs/*). Slugs/codes flow through unchanged into the recalc
 * payload — no client-side mapping. */

import { useEffect, useMemo, useRef, useState } from 'react';
import { catalogs as catalogsApi } from './api/catalogs.js';
import { Button, ChipsField, Field, Logo, Segmented, Toggle } from './components.jsx';
import { ROLE_CATEGORY_LABELS, predictorLabel } from './constants.js';
import { Icon } from './icons.jsx';
import { VilkaMark } from './mark.jsx';
import { Delta, Distribution, RangeNumber, RecRow } from './result.jsx';

/**
 * Drives the "default" skill chips suggested under the input BEFORE the user
 * types anything.
 *
 * Mixed-formula logic. For each candidate skill `c` (= any catalog skill that
 * isn't already picked), the score is
 *
 *     score(c) = α · popularity_role(c) + (1 - α) · relatedness(c, picked)
 *     α = 1 / (1 + 0.5 · n)        where n = pickedSlugs.length
 *
 * - With n=0 the role dominates entirely (α=1) — same as the cold-start
 *   "top role skills" experience.
 * - With every additional picked skill α shrinks (n=1 → 0.67, n=2 → 0.50,
 *   n=5 → 0.29, n=10 → 0.17), so already-picked skills smoothly take over.
 * - relatedness is the AVERAGE Jaccard weight across the user's picks.
 *   Skills with zero links to a particular pick contribute 0 to the average,
 *   which means hub-skills like Git that pair with everything naturally
 *   bubble up — exactly the behaviour we want.
 * - popularity_role(c) is 0 for skills HH didn't see in this role, but those
 *   skills can still surface via the related-component (the formula doesn't
 *   require both signals to be non-zero).
 *
 * Both fetches are cached in a ref so role/skill flips don't re-hit the
 * network for known slugs.
 */
const ROLE_DECAY_K = 0.5;

function useSkillSuggestions(roleSlug, pickedSlugs) {
  const [related, setRelated] = useState({}); // pickedSlug -> [{ skill: {slug,...}, weight }]
  const [rolePopularity, setRolePopularity] = useState({}); // roleSlug -> { skillSlug: popularity }
  const cacheRef = useRef({ related: new Map(), role: new Map() });

  // Fetch related-skills for any picked slug we don't already have cached.
  useEffect(() => {
    let cancelled = false;
    const wanted = pickedSlugs.filter((s) => !cacheRef.current.related.has(s));
    if (wanted.length === 0) return undefined;
    Promise.all(
      wanted.map(async (slug) => {
        try {
          const list = await catalogsApi.relatedSkills(slug, 30);
          return [slug, list];
        } catch {
          return [slug, []];
        }
      })
    ).then((pairs) => {
      if (cancelled) return;
      pairs.forEach(([slug, list]) => cacheRef.current.related.set(slug, list));
      setRelated((prev) => {
        const next = { ...prev };
        pairs.forEach(([slug, list]) => { next[slug] = list; });
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [pickedSlugs]);

  // Fetch the role's HH-popularity edges; needed for the role-component of
  // the score (and as the cold-start list when nothing is picked).
  useEffect(() => {
    if (!roleSlug) return undefined;
    if (cacheRef.current.role.has(roleSlug)) return undefined;
    let cancelled = false;
    catalogsApi.role(roleSlug).then((data) => {
      if (cancelled) return;
      const map = {};
      for (const e of data?.skills || []) {
        map[e.skill.slug] = e.popularity_relevance ?? 0;
      }
      cacheRef.current.role.set(roleSlug, map);
      setRolePopularity((prev) => ({ ...prev, [roleSlug]: map }));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [roleSlug]);

  return useMemo(() => {
    const picked = new Set(pickedSlugs);
    const n = pickedSlugs.length;
    const alpha = 1 / (1 + ROLE_DECAY_K * n);
    const rolePop = (roleSlug && rolePopularity[roleSlug]) || {};

    // Build the universe of candidates: everything HH knows for the role,
    // plus every related-skill of any pick. Filter out already-picked.
    const candidates = new Set();
    Object.keys(rolePop).forEach((s) => candidates.add(s));
    for (const s of pickedSlugs) {
      for (const r of related[s] || []) candidates.add(r.skill.slug);
    }
    for (const s of picked) candidates.delete(s);

    // Index related-edges by candidate for fast lookup.
    // candidate -> [weight from each pick that links to it; 0 implied if absent]
    const edgesByCandidate = new Map();
    for (const s of pickedSlugs) {
      for (const r of related[s] || []) {
        if (!candidates.has(r.skill.slug)) continue;
        const arr = edgesByCandidate.get(r.skill.slug) || [];
        arr.push(r.weight);
        edgesByCandidate.set(r.skill.slug, arr);
      }
    }

    const scored = [...candidates].map((slug) => {
      const pop = rolePop[slug] ?? 0;
      let related_avg = 0;
      if (n > 0) {
        // Average across ALL picks (missing links count as 0). This rewards
        // hub-skills that pair with most picks, which matches the user's ask.
        const weights = edgesByCandidate.get(slug) || [];
        const total = weights.reduce((acc, w) => acc + w, 0);
        related_avg = total / n;
      }
      const score = alpha * pop + (1 - alpha) * related_avg;
      return [slug, score];
    });

    scored.sort((a, b) => b[1] - a[1]);
    return scored.filter(([, s]) => s > 0).map(([slug]) => slug);
  }, [related, rolePopularity, roleSlug, pickedSlugs]);
}


function SplitHeader({ onHome, onOpenHistory, hasHistory, headerAnim = 'hover', headerAnimTrigger }) {
  return (
    <header className="app-header">
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <Logo size={36} anim={headerAnim} trigger={headerAnimTrigger} onClick={onHome} />
        <span
          className="t-muted mobile-hide"
          style={{
            fontSize: 14,
            lineHeight: 1,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            position: 'relative',
            top: 2,
          }}
        >
          <Icon name="chevron-right" size={14} stroke={2} />
          Резюме
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Button
          variant="ghost"
          size="sm"
          onClick={onOpenHistory}
          disabled={!hasHistory}
        >
          История
          {hasHistory > 0 && (
            <span style={{
              marginLeft: 4,
              background: 'var(--t-black)', color: '#fff',
              borderRadius: 9999, fontSize: 11, padding: '2px 6px', fontWeight: 600,
            }}>{hasHistory}</span>
          )}
        </Button>
        {/* TODO(post-hackathon): restore login button — see landing.jsx. */}
      </div>
    </header>
  );
}

function ResumeForm({
  catalogs, form, setForm, fieldGlow, onRecalc, onClear, onSaveVersion,
  canSave, dirty, formValid, isLoading,
}) {
  const update = (patch) => setForm({ ...form, ...patch });
  const setSkills = (skillSlugs) => update({ skills: skillSlugs });
  const isGlow = (key) => (fieldGlow?.key === key ? fieldGlow.token : null);

  const skillBySlug = useMemo(
    () => new Map(catalogs.skills.map((s) => [s.slug, s])),
    [catalogs.skills]
  );
  // Two distinct uses of the catalog here:
  //   - searchPool: every known skill, used by ChipsField to satisfy
  //     case-insensitive substring lookups (the user types "react" and the
  //     chip-completion picks the right slug).
  //   - rankedSuggestions: the curated default list shown WITHOUT typing —
  //     populated by useSkillSuggestions below from the related-skills map
  //     (and the role's HH-popularity edges as a cold-start fallback).
  const searchPool = useMemo(
    () => catalogs.skills.map((s) => s.slug),
    [catalogs.skills]
  );
  const rankedSuggestions = useSkillSuggestions(form.role, form.skills);
  // ChipsField uses the merged list: the ranked picks come first, then the
  // rest of the catalog backs the search box. ChipsField itself handles
  // dedup-against-values and the substring filter.
  const skillSuggestions = useMemo(() => {
    const seen = new Set(rankedSuggestions);
    return [...rankedSuggestions, ...searchPool.filter((s) => !seen.has(s))];
  }, [rankedSuggestions, searchPool]);

  return (
    <div className="stack-6" style={{ paddingBottom: 120 }}>
      <div className="card-flat" style={{ padding: 0 }}>
        <div className="stack-6">

          <Field label="Желаемая роль" ariaId="field-role">
            <div className={isGlow('role') ? 'field-glow' : ''} key={isGlow('role') || 'role'}>
              <select
                id="field-role"
                className="input select"
                value={form.role}
                onChange={(e) => update({ role: e.target.value })}
              >
                <option value="" disabled>Выберите роль…</option>
                {/* Roles arrive already sorted by (category-rank, name); we
                    just slice the stream into <optgroup>s as the category
                    changes. New/unknown categories fall through into a
                    generic «Другое» bucket — safer than dropping them. */}
                {Object.entries(
                  catalogs.roles.reduce((acc, r) => {
                    const key = r.category || 'other';
                    (acc[key] = acc[key] || []).push(r);
                    return acc;
                  }, {}),
                ).map(([key, roles]) => (
                  <optgroup key={key} label={ROLE_CATEGORY_LABELS[key] || 'Другое'}>
                    {roles.map((r) => (
                      <option key={r.slug} value={r.slug}>{r.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          </Field>

          <Field label="Опыт работы" ariaId="field-experience">
            <div className={isGlow('experience') ? 'field-glow' : ''} key={isGlow('experience') || 'exp'}>
              <Segmented
                value={form.experience}
                onChange={(v) => update({ experience: v })}
                options={catalogs.experience.map((e) => ({ value: e.code, label: e.label }))}
              />
            </div>
          </Field>

          <Field
            label="Ключевые навыки"
            hint={
              form.role
                ? "Выбирайте из подсказок или начните набирать название."
                : "Сначала выберите роль — мы подберём навыки под неё."
            }
            ariaId="field-skills"
          >
            <ChipsField
              values={form.skills}
              onChange={setSkills}
              suggestions={skillSuggestions}
              labelOf={(slug) => skillBySlug.get(slug)?.name || slug}
              isKnown={(slug) => skillBySlug.has(slug)}
              glowToken={isGlow('skills')}
              disabled={!form.role}
              disabledHint="Сначала выберите роль"
            />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 24, alignItems: 'end' }} className="mobile-stack">
            <Field label="Регион" ariaId="field-region">
              <div className={isGlow('region') ? 'field-glow' : ''} key={isGlow('region') || 'reg'}>
                <select
                  id="field-region"
                  className="input select"
                  value={form.region || ''}
                  onChange={(e) => update({ region: e.target.value || null })}
                >
                  {catalogs.regions.map((r) => (
                    <option key={r.slug} value={r.slug}>{r.name}</option>
                  ))}
                </select>
              </div>
            </Field>
            <div style={{ height: 48, display: 'flex', alignItems: 'center' }}>
              <Toggle
                checked={form.remote}
                onChange={(v) => update({ remote: v })}
                label="Удалёнка возможна"
              />
            </div>
          </div>

          <Field label="Тип занятости" ariaId="field-employment">
            <div className={isGlow('employment') ? 'field-glow' : ''} key={isGlow('employment') || 'emp'}>
              <Segmented
                value={form.employment || 'full'}
                onChange={(v) => update({ employment: v })}
                options={(catalogs.employment || []).map((e) => ({ value: e.code, label: e.label }))}
              />
            </div>
          </Field>

          <CollapsibleField
            label="Иностранные языки"
            glow={isGlow('languages')}
            expandedByDefault={(form.languages || []).length > 0}
          >
            <LanguagesEditor
              value={form.languages || []}
              onChange={(v) => update({ languages: v })}
              languages={catalogs.languages || []}
              levels={catalogs.languageLevels || []}
            />
          </CollapsibleField>

          <CollapsibleField
            label="Образование"
            glow={isGlow('education')}
            expandedByDefault={!!form.education}
          >
            <input
              id="field-education"
              className="input"
              placeholder="Например, МГТУ им. Баумана, бакалавр, 2020"
              value={form.education}
              onChange={(e) => update({ education: e.target.value })}
            />
          </CollapsibleField>

        </div>
      </div>

      <div
        className="mobile-fixed-cta"
        style={{
          position: 'sticky', bottom: 0, background: 'rgba(255,255,255,0.92)',
          backdropFilter: 'saturate(180%) blur(10px)',
          padding: '16px 0', borderTop: '1px solid var(--t-border)',
          zIndex: 5,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Button
            variant="primary"
            icon={isLoading ? 'spinner' : undefined}
            onClick={onRecalc}
            disabled={!formValid || isLoading}
          >
            {isLoading ? 'Считаем…' : 'Пересчитать вилку'}
          </Button>
          <Button variant="ghost" onClick={onClear}>Очистить</Button>
          {dirty && (
            <div className="t-muted" style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: 99, background: 'var(--t-warning)' }}></span>
              Данные изменились — пересчитайте
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* Compact list editor for the optional Languages block.
 *
 * Value shape: [{ language: 'en', level: 'b1' }, ...]
 * - Empty list = collapsed state with just a "+ Добавить язык" button.
 * - Each row: language dropdown + level dropdown + remove button.
 * - First level defaults to B1 (most common pick); language defaults to
 *   the first not-yet-used option so duplicate rows are unlikely. */
function LanguagesEditor({ value, onChange, languages, levels }) {
  const used = new Set(value.map((v) => v.language));
  const nextFreeLanguage = () =>
    (languages.find((l) => !used.has(l.code)) || languages[0])?.code;
  const defaultLevel =
    levels.find((l) => l.code === 'b1')?.code || levels[1]?.code || levels[0]?.code;

  const addRow = () => {
    const code = nextFreeLanguage();
    if (!code) return;
    onChange([...value, { language: code, level: defaultLevel }]);
  };
  const setRow = (idx, patch) => {
    onChange(value.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };
  const removeRow = (idx) => onChange(value.filter((_, i) => i !== idx));

  return (
    <div className="stack-2">
      {value.map((row, idx) => (
        <div
          key={idx}
          style={{ display: 'flex', gap: 8, alignItems: 'center' }}
        >
          <select
            className="input select"
            value={row.language}
            onChange={(e) => setRow(idx, { language: e.target.value })}
            style={{ flex: 1, minWidth: 0 }}
          >
            {languages.map((l) => (
              <option key={l.code} value={l.code}>{l.label}</option>
            ))}
          </select>
          <select
            className="input select"
            value={row.level}
            onChange={(e) => setRow(idx, { level: e.target.value })}
            style={{ flex: '0 0 140px' }}
          >
            {levels.map((l) => (
              <option key={l.code} value={l.code}>{l.label}</option>
            ))}
          </select>
          <button
            type="button"
            className="chip-x"
            onClick={() => removeRow(idx)}
            aria-label="Удалить язык"
            style={{ width: 32, height: 32 }}
          >
            <Icon name="x" size={14} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addRow}
        disabled={!languages.length}
        style={{
          background: 'transparent', border: '1px dashed var(--t-border)',
          padding: '8px 14px', borderRadius: 'var(--t-radius-s)',
          cursor: 'pointer', alignSelf: 'flex-start',
          color: 'var(--t-text)', fontSize: 14, fontWeight: 500,
        }}
      >
        + Добавить язык
      </button>
    </div>
  );
}

function CollapsibleField({ label, children, expandedByDefault, glow }) {
  const [open, setOpen] = useState(!!expandedByDefault);
  return (
    <div className={glow ? 'field-glow' : ''} key={glow || 'col'}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          background: 'transparent', border: 0, padding: 0, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 6, color: 'var(--t-text)',
          fontWeight: 500, fontSize: 14,
        }}
      >
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={16} />
        {label}
        <span style={{ color: 'var(--t-text-muted)', fontWeight: 400, marginLeft: 4 }}>не обязательно</span>
      </button>
      {open && (
        <div style={{ marginTop: 12 }}>
          {children}
        </div>
      )}
    </div>
  );
}

function ResultEmpty({ completion = 0 }) {
  const msg =
    completion < 40 ? 'Заполните основные поля слева, чтобы получить вилку дохода'
    : completion < 80 ? 'Уже неплохо. Добавьте опыт или навыки — оценка станет точнее'
    : 'Можно жать «Пересчитать вилку» — данных достаточно';
  return (
    <div
      className="card"
      style={{ padding: 32, textAlign: 'center', minHeight: 320, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14 }}
    >
      <VilkaMark variant="yellow-square" size={88} anim="progress" progress={completion} />
      <div className="t-h3 t-tnum" style={{ margin: 0 }}>Резюме готово на {completion}%</div>
      <div className="t-body t-muted" style={{ maxWidth: 320 }}>{msg}</div>
    </div>
  );
}

function ResultLoading() {
  return (
    <div className="card stack-4">
      <div className="skeleton" style={{ width: 140, height: 14 }}></div>
      <div className="skeleton" style={{ width: '80%', height: 56 }}></div>
      <div className="skeleton" style={{ width: 200, height: 28, borderRadius: 9999 }}></div>
      <div className="skeleton" style={{ width: '100%', height: 56 }}></div>
      <div className="skeleton" style={{ width: 160, height: 14 }}></div>
      <div style={{ height: 1, background: 'var(--t-border)', margin: '8px 0' }}></div>
      <div className="skeleton" style={{ width: 180, height: 18 }}></div>
      {[0,1,2,3].map((i) => (
        <div key={i} className="skeleton" style={{ width: '100%', height: 64 }}></div>
      ))}
    </div>
  );
}

function ResultError({ error, onRetry }) {
  const isValidation = error && ['unknown_role', 'unknown_skills', 'unknown_region', 'validation_error'].includes(error.code);
  const msg = error && error.message
    ? error.message
    : 'Похоже, что-то пошло не так. Попробуйте ещё раз — это бесплатно.';
  return (
    <div className="card stack-4" style={{ textAlign: 'center', padding: 32 }}>
      <div style={{
        width: 56, height: 56, borderRadius: '50%',
        background: '#FEE6E6', display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto', color: 'var(--t-error)',
      }}>
        <Icon name="alert" size={26} stroke={2} />
      </div>
      <div className="t-h3" style={{ margin: 0 }}>
        {isValidation ? 'Проверьте поля резюме' : 'Не удалось рассчитать'}
      </div>
      <div className="t-body t-muted">{msg}</div>
      <Button variant="secondary" icon="refresh" onClick={onRetry} style={{ margin: '0 auto' }}>Повторить</Button>
    </div>
  );
}

const TOP_N_RECS = 5;

/**
 * Pick the top-N recs to display, given the current ranking mode + the set of
 * skills already on the resume.
 *
 * - rule-based recs (no popularityRelevance/costRelevance) always sort to the
 *   END regardless of mode, so skill suggestions take precedence;
 * - skill recs already in the resume are filtered out;
 * - in 'cost' mode, skills with null costRelevance fall back to popularity so
 *   the list stays full while ML weights aren't plugged in yet.
 */
function rankRecs(recs, formSkills, mode) {
  const picked = new Set(formSkills || []);
  const filtered = recs.filter((r) => !(r.field === 'skills' && picked.has(r.payload)));
  const weight = (r) => {
    if (r.popularityRelevance == null && r.costRelevance == null) return -1;
    const cost = r.costRelevance ?? r.popularityRelevance ?? 0;
    return mode === 'cost' ? cost : (r.popularityRelevance ?? 0);
  };
  return [...filtered].sort((a, b) => weight(b) - weight(a)).slice(0, TOP_N_RECS);
}

/**
 * Cascade-refresh animation when the displayed list changes.
 *
 * Two phases:
 *   1) dim the currently-rendered cards top-to-bottom (stagger);
 *   2) swap to the new list, then un-dim cascade.
 *
 * Triggers only on mode change (not on every form re-render). When the user
 * adds/removes a skill, the list updates without animation — that's a different
 * interaction, the cascade would feel noisy there.
 */
function useCascadeOnModeChange(mode, items) {
  const [shown, setShown] = useState(items);
  const [phase, setPhase] = useState('idle'); // idle | dimming | reviving
  const lastModeRef = useRef(mode);

  useEffect(() => {
    // Items changed but mode is the same -> swap silently (skill picked etc).
    if (lastModeRef.current === mode) {
      setShown(items);
      return undefined;
    }
    lastModeRef.current = mode;

    const stagger = 60;
    const halfDur = 180;
    const oldCount = shown.length;

    setPhase('dimming');
    const t1 = setTimeout(() => {
      setShown(items);
      setPhase('reviving');
      const t2 = setTimeout(
        () => setPhase('idle'),
        items.length * stagger + halfDur
      );
      return () => clearTimeout(t2);
    }, oldCount * stagger + halfDur);

    return () => clearTimeout(t1);
    // shown is intentionally not a dep: animation reads the snapshot at start.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, items]);

  return { shown, phase };
}

function RecModeToggle({ mode, onChange, costAvailable }) {
  const opts = [
    { value: 'popularity', label: 'По популярности' },
    { value: 'cost', label: 'По стоимости', disabled: !costAvailable },
  ];
  return (
    <div className="rec-mode-toggle">
      {opts.map((o) => (
        <button
          key={o.value}
          type="button"
          className={'rec-mode-btn' + (mode === o.value ? ' is-active' : '')}
          disabled={o.disabled}
          title={o.disabled ? 'Доступно после подключения ML-весов' : undefined}
          onClick={() => !o.disabled && onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function ResultPanel({
  result, prevResult, recs, formSkills, onPickRec, activeRecId,
  onSaveVersion, onOpenHistory, canSave, isLoading, error, onRetry, isEmpty,
  pulseToken, completion, dirty,
}) {
  if (isLoading) return <ResultLoading />;
  if (error) return <ResultError error={error} onRetry={onRetry} />;
  if (isEmpty) return <ResultEmpty completion={completion} />;

  const deltaMid = prevResult ? result.mid - prevResult.mid : null;

  return (
    <div className="stack-6">
      <div
        className={'card ' + (pulseToken ? 'pulse-recalc' : '')}
        style={{ animation: pulseToken && deltaMid > 0 ? 'pulseRecalc 320ms var(--t-ease), glowUp 600ms var(--t-ease)' : undefined }}
        key={pulseToken}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <span className="t-caption t-muted">Вилка дохода в месяц</span>
          <span style={{ fontSize: 12, color: 'var(--t-text-muted)' }}>
            <span style={{
              display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
              background: dirty ? 'var(--t-warning)' : 'var(--t-success)',
              marginRight: 6, verticalAlign: 'middle',
            }}></span>
            {dirty ? 'Данные изменились' : 'Свежий расчёт'}
          </span>
        </div>

        <RangeNumber min={result.min} max={result.max} />

        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {deltaMid != null && <Delta value={deltaMid} animKey={pulseToken} />}
        </div>

        <div style={{ marginTop: 20 }}>
          <Distribution min={result.min} max={result.max} mid={result.mid} />
          <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--t-text-muted)' }}>
            <span>60 000 ₽</span>
            {result.marketPercentile != null && (
              <span>Вы в топ {Math.max(1, Math.round((1 - result.marketPercentile) * 100))}% IT-рынка</span>
            )}
            <span>500 000+ ₽</span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 20, paddingTop: 20, borderTop: '1px solid var(--t-border)' }}>
          <Button variant="secondary" size="sm" icon="save" onClick={onSaveVersion} disabled={!canSave} block>Сохранить версию</Button>
          <Button variant="secondary" size="sm" icon="compare" onClick={onOpenHistory} block>Сравнить версии</Button>
        </div>

        {/* Provenance line. The same calculation run through different
            predictors will return different numbers, so showing which one
            produced this result keeps users from comparing apples to
            oranges later in the history. */}
        {result.predictorVersion && (
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--t-text-muted)', textAlign: 'center' }}>
            Модель: {predictorLabel(result.predictorVersion)}
          </div>
        )}
      </div>

      <RecsCard
        recs={recs}
        formSkills={formSkills}
        onPickRec={onPickRec}
        activeRecId={activeRecId}
      />
    </div>
  );
}

// How long the rec row stays in the DOM while collapsing — must match the
// CSS .rec-cascade.is-departing transition duration. onPickRec fires a bit
// earlier so the chip flash on the left appears as the row finishes fading.
const RECS_DEPART_MS = 240;
const RECS_PICK_DELAY_MS = 120;

function RecsCard({ recs, formSkills, onPickRec, activeRecId }) {
  const [mode, setMode] = useState('popularity');
  // Cost mode is gated on at least one skill having a non-null cost weight;
  // until ML supplies them this stays false and the toggle's "По стоимости"
  // option is disabled with an explanatory tooltip.
  const costAvailable = useMemo(
    () => recs.some((r) => r.costRelevance != null),
    [recs]
  );
  const ranked = useMemo(
    () => rankRecs(recs, formSkills, mode),
    [recs, formSkills, mode]
  );
  const { shown, phase } = useCascadeOnModeChange(mode, ranked);
  // Recs that the user has clicked but whose state-update is still in-flight.
  // The row gets .is-departing → CSS fades+collapses it in place. After
  // RECS_DEPART_MS we drop it from the set so the cascade below has clean
  // ground to FLIP into.
  const [departing, setDeparting] = useState(new Set());

  const handlePickRec = (rec) => {
    if (departing.has(rec.id)) return;
    // Rule-based recs (language/remote/etc) don't add a skill chip — just
    // forward without the fade-out choreography.
    if (rec.field !== 'skills') { onPickRec(rec); return; }

    setDeparting((prev) => { const n = new Set(prev); n.add(rec.id); return n; });
    // Fire onPickRec mid-fade so the chip flash on the left lights up while
    // the row is still visibly leaving — feels like one continuous action.
    setTimeout(() => onPickRec(rec), RECS_PICK_DELAY_MS);
    setTimeout(() => {
      setDeparting((prev) => { const n = new Set(prev); n.delete(rec.id); return n; });
    }, RECS_DEPART_MS);
  };

  return (
    <div className="card stack-3">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div className="t-h3" style={{ margin: 0 }}>Как повысить вилку</div>
        <RecModeToggle mode={mode} onChange={setMode} costAvailable={costAvailable} />
      </div>
      <div className="stack-2 rec-list" data-phase={phase}>
        {shown.map((rec, i) => (
          <div
            key={rec.id || rec.slug}
            className={
              'rec-cascade'
              + (phase === 'dimming' ? ' is-dim' : phase === 'reviving' ? ' is-revive' : '')
              + (departing.has(rec.id) ? ' is-departing' : '')
            }
            style={{ '--cascade-i': i }}
          >
            <RecRow
              rec={rec}
              isActive={activeRecId === rec.id}
              onClick={() => handlePickRec(rec)}
            />
          </div>
        ))}
        {shown.length === 0 && (
          <div className="t-body t-muted" style={{ padding: 12 }}>
            Резюме выглядит крепко — крупных рекомендаций нет.
          </div>
        )}
      </div>
    </div>
  );
}

export function SplitView({
  catalogs, form, setForm, result, prevResult, recs,
  onRecalc, onClear, onSaveVersion, onPickRec, activeRecId,
  onHome, onOpenHistory, hasHistory, fieldGlow,
  isLoading, error, isEmpty, dirty, pulseToken,
  completion, headerAnim, headerAnimTrigger,
}) {
  // Role is required by the backend; allow Recalculate only when picked.
  const formValid = !!form.role;
  const canSave = !isEmpty && !isLoading && !error;

  return (
    <div className="screen-enter">
      <SplitHeader
        onHome={onHome}
        onOpenHistory={onOpenHistory}
        hasHistory={hasHistory}
        headerAnim={headerAnim}
        headerAnimTrigger={headerAnimTrigger}
      />
      <div className="container" style={{ padding: '32px 24px 0' }}>
        <div
          className="split-grid"
          style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr)', gap: 40 }}
        >
          <div>
            <div style={{ marginBottom: 20 }}>
              <h1 className="t-h1" style={{ margin: 0 }}>Расскажите о себе</h1>
              <p className="t-body t-muted" style={{ margin: '8px 0 0' }}>
                Чем подробнее заполните — тем точнее будет вилка. Все поля можно править и пересчитывать сколько угодно раз.
              </p>
            </div>
            <ResumeForm
              catalogs={catalogs}
              form={form}
              setForm={setForm}
              fieldGlow={fieldGlow}
              onRecalc={onRecalc}
              onClear={onClear}
              onSaveVersion={onSaveVersion}
              canSave={canSave}
              dirty={dirty}
              formValid={formValid}
              isLoading={isLoading}
            />
          </div>
          <div>
            <div className="sticky-panel" style={{ position: 'sticky', top: 88 }}>
              <ResultPanel
                result={result}
                prevResult={prevResult}
                recs={recs}
                formSkills={form.skills}
                onPickRec={onPickRec}
                activeRecId={activeRecId}
                onSaveVersion={onSaveVersion}
                onOpenHistory={onOpenHistory}
                canSave={canSave}
                isLoading={isLoading}
                error={error}
                onRetry={onRecalc}
                isEmpty={isEmpty}
                pulseToken={pulseToken}
                completion={completion}
                dirty={dirty}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
